import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { POLICYHOLDER_ROLE } from '../../policies/model/partners';

/**
 * View models for the Gruppen-Policen surface.
 *
 * # The word this file may not use
 *
 * "Version". Not in a label, not in a column header, not in a tooltip (SSOT I-6, service summary
 * §6 phase 2). A Group Policy DOES carry a `businessVersion` and the surface has to be honest
 * about change over time — it says so in the operations persona's own words: "Stand" for the
 * belief being read, "Zeitverlauf" for the segments. Configuration is where versions live.
 */

/** protojson writes enum NAMES prefixed with the enum's own name; the UI wants the bare value. */
export function bareEnum(value: string | undefined, prefix: string): string {
  if (value === undefined || value === '') return '';
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

export const groupPolicyStatusOf = (groupPolicy: InstanceApi.GroupPolicy | undefined): string =>
  bareEnum(groupPolicy?.status, 'GROUP_POLICY_STATUS_');

/**
 * What to render in a policyholder cell, and whether it is a NAME or an id.
 *
 * The service answers with an explicit unresolved flag rather than an empty string, because the
 * two claims are different: "this partner has no name" is false, and "nobody here knows this
 * partner" is true whenever the partner directory is unprovisioned — which is every deployment
 * today (SSOT §4.2). Rendering the id is how the second claim is said out loud; a blank cell
 * would say the first.
 */
export interface PolicyholderLabel {
  readonly text: string;
  readonly resolved: boolean;
}

export function policyholderLabel(entry: InstanceApi.GroupPolicyListEntry): PolicyholderLabel {
  const id = entry.groupPolicy?.policyholderPartnerId ?? '';
  const name = entry.partnerName ?? '';
  return entry.partnerNameResolved === true && name !== ''
    ? { text: name, resolved: true }
    : { text: id, resolved: false };
}

/**
 * The agreement's policyholder as its header and its members tab show it: the name the
 * directory gave for the policyholder link in `partners` (SDD-011 P-5), the id when nobody knew.
 * `GetGroupPolicy` resolves its parties since SDD-014 — this is the read of that answer.
 */
export function groupPolicyPolicyholderLabel(
  groupPolicy: InstanceApi.GroupPolicy | undefined,
): PolicyholderLabel {
  const id = groupPolicy?.policyholderPartnerId ?? '';
  const partners = groupPolicy?.partners ?? [];
  const match =
    partners.find(
      (partner) => partner.link?.roleErn === POLICYHOLDER_ROLE && partner.link?.partnerId === id,
    ) ?? partners.find((partner) => partner.link?.partnerId === id);
  const name = match?.partnerName ?? '';
  return match?.partnerNameResolved === true && name !== ''
    ? { text: name, resolved: true }
    : { text: id, resolved: false };
}

/** A row of the list, flattened for the grid. */
export interface GroupPolicyRow {
  readonly id: string;
  readonly code: string;
  readonly status: string;
  readonly policyholderPartnerId: string;
  readonly partnerName: string;
  readonly partnerNameResolved: boolean;
  readonly activePolicyCount: number;
  readonly validFrom: string;
}

export function toGroupPolicyRows(
  entries: readonly InstanceApi.GroupPolicyListEntry[] | undefined,
): GroupPolicyRow[] {
  return (entries ?? []).map((entry) => {
    const label = policyholderLabel(entry);
    return {
      id: entry.groupPolicy?.code ?? '',
      code: entry.groupPolicy?.code ?? '',
      status: groupPolicyStatusOf(entry.groupPolicy),
      policyholderPartnerId: entry.groupPolicy?.policyholderPartnerId ?? '',
      partnerName: label.text,
      partnerNameResolved: label.resolved,
      // protojson renders int32 as a number, but a proto3 default is omitted entirely — an
      // agreement with no members has no field at all, not a zero.
      activePolicyCount: entry.activePolicyCount ?? 0,
      validFrom: entry.snapshot?.validFrom ?? '',
    };
  });
}

/**
 * The segments a reader should see, newest belief first, with superseded ones hidden by default.
 *
 * Superseded is never deleted (decision S-6) — GoBD and premium reproducibility need every
 * historical belief reconstructable — but it is also not what somebody opening a Gruppen-Police
 * is asking about. Hiding it by default and offering it explicitly is the difference between an
 * audit tool and a screen that shows four rows where one is true.
 */
export function visibleSegments(
  segments: readonly InstanceApi.GroupPolicySegment[] | undefined,
  includeSuperseded: boolean,
): readonly InstanceApi.GroupPolicySegment[] {
  const all = segments ?? [];
  return includeSuperseded ? all : all.filter((segment) => segment.snapshot?.current !== false);
}

export const isSuperseded = (segment: InstanceApi.GroupPolicySegment): boolean =>
  segment.snapshot?.current === false;

/** The billing block, with the one field the domain does not let a user choose (SSOT I-20). */
export const DUNNING_MASTER_ONLY = 'MASTER_ONLY';

export const defaultBilling: InstanceApi.BillingConfig = {
  mode: 'AGGREGATE_CHILD_PREMIUMS',
  frequency: 'MONTHLY',
  dunning: DUNNING_MASTER_ONLY,
};
